/**
 * Занятие режимов «Скорость» и «Точность».
 *
 * Форма у режимов одна — несколько коротких прогонов подряд, — а правила
 * разные, и живут они в одном месте: разводить по разным функциям то, что
 * отличается двумя условиями, значит дважды описывать одно и то же занятие.
 *
 * «Скорость» ведёт лестницу целевого темпа (ТЗ FR-EN-16): ступень поднимается
 * от двух взятых целей подряд, опускается от точности ниже порога, а чистый
 * но медленный прогон её не двигает. «Точность» засчитывает только прогон без
 * единой ошибки (ТЗ FR-EN-17): ошибка возвращает к началу элемента, а не
 * давшийся за три попытки откладывается.
 */

import type { MasteryUnit, UnitOutcome } from '@type2x/shared-contracts';
import { EMPTY_METRICS, toMasteryUnitId } from '@type2x/shared-contracts';
import {
  EMPTY_OBSERVATION,
  collectSegmentOutcomes,
  mergeMetrics,
  mergeObservations,
  type RunObservation,
} from '@type2x/typing-engine';
import { characterFrequency } from '@type2x/corpora';
import { createRng } from '@type2x/exercise-generator';

import type {
  TempoDrill,
  TempoMode,
  TempoRun,
  TempoSession,
  TempoSnapshot,
  TempoVerdict,
} from '../contracts/tempo.js';
import {
  ACCURACY_DRILLS,
  ACCURACY_PACE_RATIO,
  ACCURACY_WORDS_PER_DRILL,
  HINT_LEVEL,
  HOLDS_TO_ADVANCE,
  MAX_ATTEMPTS_PER_DRILL,
  MIN_TARGET_CPM,
  SPEED_MIN_ACCURACY,
  SPEED_RUNS,
  STEP_CPM,
  WORDS_PER_RUN,
} from '../contracts/tempo.js';
import { tempoWords } from './material.js';

/** Округляет ступень до шага лестницы: промежуточных значений у неё нет. */
function toStep(cpm: number): number {
  return Math.max(MIN_TARGET_CPM, Math.round(cpm / STEP_CPM) * STEP_CPM);
}

/**
 * Первая ступень лестницы.
 *
 * Она берётся от измеренной скорости, а не от круглого числа: цель ниже уже
 * достигнутого не тренирует, а заметно выше — не берётся ни разу.
 */
export function initialTargetCpm(snapshot: TempoSnapshot): number {
  if (snapshot.targetCpm !== null) return toStep(snapshot.targetCpm);
  return toStep(snapshot.baselineCpm + STEP_CPM);
}

/** Собирает занятие режима. */
export function startTempoSession(
  snapshot: TempoSnapshot,
  mode: TempoMode,
  seed: string,
): TempoSession {
  const rng = createRng(seed);
  const pool = rng.shuffle([...tempoWords(snapshot.language, mode)]);

  const drillCount = mode === 'speed' ? SPEED_RUNS : ACCURACY_DRILLS;
  const wordsPerDrill = mode === 'speed' ? WORDS_PER_RUN : ACCURACY_WORDS_PER_DRILL;

  const drills: TempoDrill[] = [];
  for (let index = 0; index < drillCount; index += 1) {
    const words: string[] = [];
    for (let offset = 0; offset < wordsPerDrill; offset += 1) {
      // Пул перебирается по кругу: слов в нём заведомо больше, чем в одном
      // занятии, но зацикливание защищает от короткого словаря.
      words.push(pool[(index * wordsPerDrill + offset) % pool.length] ?? '');
    }
    drills.push({ words, text: words.join(' '), attempts: 0, cleared: false, deferred: false });
  }

  const ladder = initialTargetCpm(snapshot);

  return {
    mode,
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    ladderCpm: ladder,
    targetCpm: mode === 'speed' ? ladder : Math.round(ladder * ACCURACY_PACE_RATIO),
    holds: 0,
    drills,
    currentIndex: 0,
    finished: drills.length === 0,
    hintLevel: HINT_LEVEL[mode],
    metrics: EMPTY_METRICS,
    errors: [],
    observed: {},
    lastVerdict: null,
  };
}

/** Элемент, который пользователь набирает сейчас. */
export function currentDrill(session: TempoSession): TempoDrill | null {
  if (session.finished) return null;
  return session.drills[session.currentIndex] ?? null;
}

/** Элементы, с которыми занятие ещё работает. */
export function remainingDrills(session: TempoSession): TempoDrill[] {
  return session.drills.filter((drill) => !drill.cleared && !drill.deferred);
}

/** Ступень лестницы, которую нужно запомнить в профиле. */
export function achievedTargetCpm(session: TempoSession): number {
  return session.ladderCpm;
}

/** Итог прогона режима «Скорость»: что случилось со ступенью. */
function speedVerdict(session: TempoSession, run: TempoRun): TempoVerdict {
  if (run.metrics.accuracy < SPEED_MIN_ACCURACY) return 'dropped';
  if (run.metrics.cpm < session.targetCpm) return 'slow';
  return session.holds + 1 >= HOLDS_TO_ADVANCE ? 'advanced' : 'held';
}

/** Новая ступень и счётчик удержаний после прогона режима «Скорость». */
function nextLadder(
  session: TempoSession,
  verdict: TempoVerdict,
): { ladderCpm: number; holds: number } {
  switch (verdict) {
    case 'advanced':
      return { ladderCpm: session.ladderCpm + STEP_CPM, holds: 0 };
    case 'held':
      return { ladderCpm: session.ladderCpm, holds: session.holds + 1 };
    case 'dropped':
      // Ступень назад: цель, на которой рассыпается точность, не цель.
      return { ladderCpm: Math.max(MIN_TARGET_CPM, session.ladderCpm - STEP_CPM), holds: 0 };
    default:
      // Темпа не хватило: ступень остаётся, но серия удержаний прерывается.
      return { ladderCpm: session.ladderCpm, holds: 0 };
  }
}

/** Наблюдения по словам прогона. */
function observeWords(
  drill: TempoDrill,
  run: TempoRun,
  observed: Readonly<Record<string, RunObservation>>,
): Record<string, RunObservation> {
  const next: Record<string, RunObservation> = { ...observed };

  for (const outcome of collectSegmentOutcomes(drill.words, run.events, run.errors)) {
    const previous = next[outcome.segment] ?? EMPTY_OBSERVATION;
    next[outcome.segment] = mergeObservations(previous, {
      attempts: outcome.attempts,
      errors: outcome.errors,
      corrections: outcome.corrections,
      medianLatencyMs: outcome.medianLatencyMs,
    });
  }

  return next;
}

/**
 * Переводит занятие вперёд по итогам прогона.
 *
 * В режиме «Скорость» элемент не повторяется: следующая попытка — это
 * следующий прогон, и лестница уже учла предыдущий. В режиме «Точность»
 * элемент повторяется с начала, пока не набран чисто либо пока не исчерпаны
 * попытки (ТЗ §6, режим 7).
 */
export function advanceTempo(session: TempoSession, run: TempoRun): TempoSession {
  const drill = currentDrill(session);
  if (drill === null) return session;

  const verdict =
    session.mode === 'speed' ? speedVerdict(session, run) : accuracyVerdict(drill, run);

  const ladder =
    session.mode === 'speed'
      ? nextLadder(session, verdict)
      : { ladderCpm: session.ladderCpm, holds: session.holds };

  const advanced: TempoDrill = {
    ...drill,
    attempts: drill.attempts + 1,
    cleared: verdict === 'clean' || verdict === 'held' || verdict === 'advanced',
    deferred: verdict === 'deferred',
  };

  const drills = session.drills.map((item, index) =>
    index === session.currentIndex ? advanced : item,
  );

  // Повторяется только элемент режима «Точность»: остальные исходы двигают
  // занятие дальше независимо от того, взят элемент или нет.
  const stays = verdict === 'retry';
  const nextIndex = stays ? session.currentIndex : session.currentIndex + 1;

  return {
    ...session,
    ladderCpm: ladder.ladderCpm,
    // Цель прогона в режиме скорости — сама ступень; в режиме точности она не
    // меняется вместе с ней, потому что и ступень там не двигается.
    targetCpm: session.mode === 'speed' ? ladder.ladderCpm : session.targetCpm,
    holds: ladder.holds,
    drills,
    currentIndex: Math.min(nextIndex, drills.length - 1),
    finished: nextIndex >= drills.length,
    metrics: mergeMetrics(session.metrics, run.metrics),
    errors: [...session.errors, ...run.errors],
    observed: observeWords(drill, run, session.observed),
    lastVerdict: verdict,
  };
}

/**
 * Итог прогона режима «Точность».
 *
 * Засчитывается только прогон без единой ошибки: высокий порог качества и
 * означает, что «почти чисто» — это не чисто. Элемент, не давшийся за
 * отведённые попытки, откладывается, иначе занятие упёрлось бы в него.
 */
function accuracyVerdict(drill: TempoDrill, run: TempoRun): TempoVerdict {
  if (run.errors.length === 0) return 'clean';
  return drill.attempts + 1 >= MAX_ATTEMPTS_PER_DRILL ? 'deferred' : 'retry';
}

/** Учебные единицы слов занятия: слово могло встретиться впервые. */
export function tempoUnits(session: TempoSession): MasteryUnit[] {
  const words = [...new Set(session.drills.flatMap((drill) => drill.words))];

  return words.map((word) => ({
    id: toMasteryUnitId(`word:${word}`),
    kind: 'word',
    content: word,
    language: session.language,
    layoutId: session.layoutId,
    realWorldFrequency: wordFrequency(session.language, word),
  }));
}

/**
 * Частота слова, приведённая к 0..1.
 *
 * Точной частоты слова у режима нет: словарь отдаёт ранг, а не счётчик.
 * Оценка по буквам достаточна для приоритета — она отличает частое слово от
 * редкого, не притворяясь измерением.
 */
function wordFrequency(language: TempoSession['language'], word: string): number {
  const letters = [...word];
  if (letters.length === 0) return 0;

  const sum = letters.reduce((total, letter) => total + characterFrequency(language, letter), 0);
  return sum / letters.length;
}

/** Итоги занятия по словам. */
export function collectTempoOutcomes(session: TempoSession): UnitOutcome[] {
  return Object.entries(session.observed).map(([word, observation]) => ({
    unitId: toMasteryUnitId(`word:${word}`),
    attempts: observation.attempts,
    errors: observation.errors,
    medianLatencyMs: observation.medianLatencyMs,
    corrections: observation.corrections,
  }));
}
